import React, { useEffect, useRef, useState } from "react";
import { USE_EXISTING } from "../../landingContent";
import SectionHeading from "../SectionHeading";

const RESOURCE_MOCKS = [
  {
    id: "google-slides",
    title: "Google Slides",
    logo: "Slides",
  },
  {
    id: "pdf",
    title: "PDFs",
    logo: "PDF",
  },
  {
    id: "powerpoint",
    title: "PowerPoint",
    logo: "P",
  },
  {
    id: "google-docs",
    title: "Google Docs",
    logo: "Docs",
  },
  {
    id: "canva",
    title: "Canva",
    logo: "C",
  },
  {
    id: "youtube",
    title: "YouTube",
    logo: "Play",
  },
];

const renderResourcePreview = (id) => {
  switch (id) {
    case "google-slides":
      return (
        <div className="landing-v2-resource-mock__slide-preview" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      );
    case "pdf":
      return (
        <div className="landing-v2-resource-mock__page-stack" aria-hidden="true">
          <span />
        </div>
      );
    case "powerpoint":
      return (
        <div className="landing-v2-resource-mock__chart-preview" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      );
    case "google-docs":
      return (
        <div className="landing-v2-resource-mock__doc-lines" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>
      );
    case "canva":
      return (
        <>
          <div className="landing-v2-resource-mock__design-preview" aria-hidden="true">
            <span className="landing-v2-resource-mock__design-shape landing-v2-resource-mock__design-shape--primary" />
            <span className="landing-v2-resource-mock__design-shape landing-v2-resource-mock__design-shape--accent" />
            <span className="landing-v2-resource-mock__design-line" />
          </div>
        </>
      );
    case "youtube":
      return (
        <>
          <div className="landing-v2-resource-mock__video-preview" aria-hidden="true">
            <span className="landing-v2-resource-mock__video-badge">
              <span className="landing-v2-resource-mock__video-play" />
            </span>
            <span className="landing-v2-resource-mock__video-progress" />
          </div>
          <span className="landing-v2-resource-mock__video-avatar" aria-hidden="true" />
          <span className="landing-v2-resource-mock__video-title-line" aria-hidden="true" />
        </>
      );
    default:
      return null;
  }
};

const UseExistingResourcesSection = ({
  headline = USE_EXISTING.headline,
  paragraphs = USE_EXISTING.paragraphs,
  strongLine = USE_EXISTING.strongLine,
  chips = USE_EXISTING.chips,
}) => {
  const mosaicRef = useRef(null);
  const [mosaicVisible, setMosaicVisible] = useState(false);

  useEffect(() => {
    const mosaicElement = mosaicRef.current;

    if (!mosaicElement || mosaicVisible) {
      return undefined;
    }

    if (!("IntersectionObserver" in window)) {
      setMosaicVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMosaicVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "0px 0px -12% 0px",
        threshold: 0.25,
      }
    );

    observer.observe(mosaicElement);

    return () => {
      observer.disconnect();
    };
  }, [mosaicVisible]);

  return (
    <section className="landing-v2__section landing-v2-resources" aria-labelledby="use-existing-heading">
      <div className="landing-v2__container">
        <div className="landing-v2__split">
          <div className="landing-v2__split-copy">
            <SectionHeading headline={headline} centered={false} headingLevel={2} />
            {paragraphs.map((paragraph) => (
              <p key={paragraph} className="landing-v2__subheading">
                {paragraph}
              </p>
            ))}
            <p className="landing-v2__strong-line">{strongLine}</p>

            <div className="landing-v2__chip-row landing-v2-resources__chip-row">
              {chips.map(({ label, id, badge }) => (
                <span
                  key={id}
                  className={`landing-v2-resources__chip landing-v2-resources__chip--${id}`}
                >
                  <span className="landing-v2-resources__chip-badge" aria-hidden="true">
                    {badge}
                  </span>
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div
            ref={mosaicRef}
            className={`landing-v2-resources__mosaic${mosaicVisible ? " is-visible" : ""}`}
            aria-label="Static previews of imported classroom resources in Lesson Launchpad"
          >
            {RESOURCE_MOCKS.map(({ id, title, logo }, index) => (
              <article
                key={id}
                className={`landing-v2-resources__mosaic-item landing-v2-resources__mosaic-item--${index + 1}`}
                aria-label={`${title} resource preview`}
              >
                <div className={`landing-v2-resource-mock landing-v2-resource-mock--${id}`}>
                  {id !== "google-slides" && id !== "google-docs" && id !== "pdf" && id !== "powerpoint" && id !== "youtube" && id !== "canva" ? (
                    <div className="landing-v2-resource-mock__topline">
                      <span className={`landing-v2-resource-mock__logo landing-v2-resource-mock__logo--${id}`} aria-hidden="true">
                        {logo}
                      </span>
                    </div>
                  ) : null}
                  {renderResourcePreview(id)}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseExistingResourcesSection;
